import { Bar, Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js'
import type { Submission } from '../../lib/types'
import { count, pct } from '../../lib/stats'
import { C } from '../../lib/colors'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend)

const BASE = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
const GRID_COLOR = 'rgba(1,87,98,0.07)'

const PANELS     = ['FSH/LH', 'Prolactin', 'Testosterone/DHEAS', 'OGTT-I (Insulin)', 'TSH/T4', 'Progesterone Day 21', 'Full Hormone Panel']
const MED_KEYS   = ['not-raised', 'told-normal', 'offered-pill', 'referred', 'investigated', 'told-lose-weight']
const MED_LABELS = ['Not raised', 'Told normal', 'Offered pill', 'Referred', 'Investigated', 'Told: lose weight']
const DISMISS    = ['told-normal', 'told-lose-weight']
const DLABS      = ['Told normal', 'Told: lose weight']

interface Props { data: Submission[] }

export function Investigations({ data }: Props) {
  const N = data.length
  const tg = [1, 2, 3, 4].map(t => data.filter(d => d.tier === t))

  const dismissDatasets = DISMISS.map((m, i) => ({
    label: DLABS[i],
    data: tg.map(g => pct(count(g, d => d.medical_response === m), g.length)),
    backgroundColor: [C.amber, C.rose][i],
    borderRadius: i === 1 ? 4 : 0,
  }))

  return (
    <section className="sec" id="investigations">
      <div className="sec-hd">
        <div className="sec-label">05 · Investigation Gaps &amp; Medical Response</div>
        <h2 className="sec-title">What isn't being tested — and how are doctors responding?</h2>
        <p className="sec-sub">investigation_gap identifies panels the system flagged as needed but not yet conducted.</p>
      </div>
      <div className="g21" style={{ marginBottom: 12 }}>
        <div className="card">
          <div className="card-title">Investigation panel frequency</div>
          <div className="card-sub">How often each panel appears in investigation_gap across the cohort</div>
          <div className="cw h260">
            <Bar
              data={{ labels: PANELS, datasets: [{ data: PANELS.map(p => pct(count(data, d => d.investigation_gap.includes(p)), N)), backgroundColor: C.domain, borderRadius: 4 }] }}
              options={{ ...BASE, indexAxis: 'y' as const, scales: { x: { max: 100, grid: { color: GRID_COLOR } }, y: { grid: { display: false } } } }}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-title">Medical response breakdown</div>
          <div className="card-sub">How respondents report previous clinical encounters</div>
          <div className="cw h260">
            <Doughnut
              data={{ labels: MED_LABELS, datasets: [{ data: MED_KEYS.map(k => count(data, d => d.medical_response === k)), backgroundColor: [C.t3, C.amber, C.a(C.rose,.6), C.mint, C.teal2, C.rose], borderWidth: 0 }] }}
              options={{ ...BASE, plugins: { legend: { display: true, position: 'bottom' as const, labels: { boxWidth: 10, padding: 8, font: { size: 10 } } } } }}
            />
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-title">Medical dismissal rate by tier</div>
        <div className="card-sub">"Dismissal" = told-normal + told-lose-weight. Does severity correlate with dismissal?</div>
        <div className="cw h220">
          <Bar
            data={{ labels: ['Tier 1 · Attuned', 'Tier 2 · Listening In', 'Tier 3 · Urgent Conv.', 'Tier 4 · Priority Review'], datasets: dismissDatasets }}
            options={{
              ...BASE,
              scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, max: 100, grid: { color: GRID_COLOR } } },
              plugins: { legend: { display: true, position: 'bottom' as const, labels: { boxWidth: 10, padding: 8, font: { size: 10 } } } },
            }}
          />
        </div>
      </div>
    </section>
  )
}
